/**
 * API client for backend communication
 */

const API_BASE = "/api";

/**
 * Send text to backend for analysis (handles both plain text and YAML)
 * Backend automatically detects and processes YAML structure
 * @param {string} text - User's input text (plain text or YAML)
 * @param {object} preferences - Optional preferences
 * @param {string} sessionId - Optional session ID for WebSocket progress tracking
 * @returns {Promise<object>} Graph data (flat or hierarchical)
 */
export async function analyzeText(text, preferences = {}, sessionId = null) {
  const body = { text, preferences };
  if (sessionId) {
    body.session_id = sessionId;
  }

  const response = await fetch(`${API_BASE}/generate`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "Failed to analyze text");
  }

  return response.json();
}

/**
 * Health check endpoint
 * @returns {Promise<object>} Health status
 */
export async function healthCheck() {
  const response = await fetch(`${API_BASE}/health`);
  return response.json();
}

/**
 * Export graph data as JSON file
 * @param {object} graphData - Graph data to export (should be pristine backend data)
 * @param {string} filename - Name of the file
 */
export function exportJSON(graphData, filename = "graph-export.json") {
  // Data is already pristine from backend, no cleaning needed
  const blob = new Blob([JSON.stringify(graphData, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Import graph data from JSON file
 * @param {File} file - JSON file to import
 * @returns {Promise<object>} Parsed graph data
 */
export async function importJSON(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result);
        resolve(data);
      } catch (error) {
        reject(new Error("Invalid JSON file"));
      }
    };
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsText(file);
  });
}
